/**
 * Custom Refine auth provider for the AOS portal.
 *
 * Auth is backend-driven: the worker owns the session cookie and exposes the
 * identity at `GET /api/auth/me`
 * (IMPLEMENTATION_CONTRACT §7). This provider never sees tokens — it only:
 *   - `login`   → `POST /api/auth/sign-in` with email/password.
 *   - `logout`  → `POST /api/auth/sign-out` revokes the session; redirect to login.
 *   - `check`   → `GET /api/auth/me`; 401 ⇒ unauthenticated ⇒ redirect to login.
 *   - `getIdentity` → the `AuthIdentity` from `/api/auth/me`.
 *   - `onError` → a 401 from any request logs out and redirects to login.
 */

import type { AuthProvider } from '@refinedev/core';
import type { AuthIdentity } from '@aos/shared';

import { ApiError, api } from '@/lib/api';
import { ROUTES } from '@/lib/routes';

/** Identity object Refine surfaces via `useGetIdentity`. */
export interface PortalIdentity extends AuthIdentity {
  /** Refine reads `name`/`avatar` for the default user menu; map from identity. */
  name: string;
}

interface LoginParams {
  email?: string;
  password?: string;
}

export const authProvider: AuthProvider = {
  login: async (params) => {
    const { email, password } = (params ?? {}) as LoginParams;
    if (!email || !password) {
      return {
        success: false,
        error: new Error('Email and password are required.'),
      };
    }

    try {
      await api.post<AuthIdentity>('/auth/sign-in', { email, password });
      return { success: true, redirectTo: ROUTES.home };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Sign-in failed.'),
      };
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/sign-out');
    } catch {
      // Even if the network call fails, treat the user as logged out locally and
      // send them to the login screen rather than trapping them in the app.
    }
    return { success: true, redirectTo: ROUTES.login };
  },

  check: async () => {
    try {
      await api.get<AuthIdentity>('/auth/me');
      return { authenticated: true };
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        return { authenticated: false, redirectTo: ROUTES.login, logout: true };
      }
      // Network/transport errors should not silently log the user out; surface
      // as unauthenticated without forcing a logout so a retry can recover.
      return {
        authenticated: false,
        redirectTo: ROUTES.login,
        error: error instanceof Error ? error : new Error('Authentication check failed'),
      };
    }
  },

  getIdentity: async (): Promise<PortalIdentity | null> => {
    try {
      const identity = await api.get<AuthIdentity>('/auth/me');
      return { ...identity, name: identity.display_name };
    } catch {
      return null;
    }
  },

  getPermissions: async (): Promise<string[]> => {
    try {
      const identity = await api.get<AuthIdentity>('/auth/me');
      // Return roles AND granular grant keys so the UI can gate on either a
      // role name (`admin`) or a fine-grained permission (`admin:users`). This
      // is back-compat (roles still included) and additive (CONTRACT §2.2).
      const grants = identity.permissions?.grants ?? [];
      return [...identity.roles, ...grants];
    } catch {
      return [];
    }
  },

  onError: async (error) => {
    if (error instanceof ApiError && error.status === 401) {
      return { logout: true, redirectTo: ROUTES.login };
    }
    if (
      typeof error === 'object' &&
      error !== null &&
      (error as { status?: number }).status === 401
    ) {
      return { logout: true, redirectTo: ROUTES.login };
    }
    return { error: error instanceof Error ? error : undefined };
  },
};

export default authProvider;
